/**
 * T557 (FR-001b) — Multi-page navigation wrapper for the expense
 * attachment preview.
 *
 * Wraps the existing single-page <ExpenseAttachmentPreview> and adds:
 *   - page-pagination bar (← Seite X / Y →)
 *   - Up/Down arrow keyboard navigation (NOT Left/Right — those are
 *     reserved for the Durcharbeite queue navigation; T558)
 *   - explicit attachment buttons on mobile (vertical gestures scroll the original)
 *   - primary "Hauptseite" badge
 *   - 1-attachment beleg hides the pagination bar entirely
 *
 * Sort order: primary attachment first, then `created_at` ascending.
 * (The `sort_order` column hinted at in the spec is a Wave-3 follow-up;
 *  for Phase-15 the createdAt fallback is the authoritative order.)
 *
 * Counter shape: "Seite 2 von 5". The Durcharbeite consumer (T558)
 * composes the beleg-queue counter ("Beleg 3 von 17") around this one.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api';
import type { ExpenseAttachmentDto } from '@armard/shared';
import { ExpenseAttachmentPreview } from './ExpenseAttachmentPreview';

export interface Props {
  attachments: ReadonlyArray<ExpenseAttachmentDto>;
  heightClass?: string;
  testid?: string;
  /** Override the keyboard handler — Durcharbeite passes false so
   * its own queue handler owns the keys, and the page-paginator
   * binds them only inside an explicit focus region. */
  enableKeyboard?: boolean;
}

function sortAttachments(attachments: ReadonlyArray<ExpenseAttachmentDto>): ExpenseAttachmentDto[] {
  return [...attachments].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
    return a.uploadedAt < b.uploadedAt ? -1 : a.uploadedAt > b.uploadedAt ? 1 : 0;
  });
}

export function ExpenseAttachmentMultiPagePreview({
  attachments,
  heightClass = 'h-[60vh]',
  testid = 'expense-attachment-multipage',
  enableKeyboard = true,
}: Props) {
  const sorted = useMemo(() => sortAttachments(attachments), [attachments]);
  const [pageIndex, setPageIndex] = useState(0);
  const attachmentIds = sorted.map((a) => a.id).join('|');

  // Reset to page 1 whenever the attachment list changes (e.g. after
  // user navigates beleg-to-beleg in Durcharbeite).
  useEffect(() => {
    setPageIndex(0);
  }, [attachmentIds]);

  const goPrev = useCallback(() => {
    setPageIndex((i) => Math.max(0, i - 1));
  }, []);
  const goNext = useCallback(() => {
    setPageIndex((i) => Math.min(sorted.length - 1, i + 1));
  }, [sorted.length]);

  // Keyboard — Up/Down so the Durcharbeite queue can keep Left/Right.
  useEffect(() => {
    if (!enableKeyboard) return;
    function onKey(e: KeyboardEvent) {
      // Don't steal arrow keys from form inputs / textareas.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable)
        return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goNext();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableKeyboard, goPrev, goNext]);

  if (sorted.length === 0) {
    return <ExpenseAttachmentPreview attachment={null} heightClass={heightClass} testid={testid} />;
  }

  const current = sorted[pageIndex] ?? sorted[0];
  const showPagination = sorted.length > 1;

  return (
    <div data-testid={testid} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="min-w-0 break-all text-white/70">
          {current.fileNameOriginal}
          {(current.pageCount ?? 0) > 1 && (
            <span className="ml-2 text-xs text-white/50">{current.pageCount} PDF-Seiten</span>
          )}
        </span>
        <a
          href={`${api.baseUrl}/expense-attachments/${current.id}`}
          className="text-sky-300 underline"
        >
          Original herunterladen
        </a>
      </div>
      {current.isPrimary && (
        <div className="flex justify-end">
          <span
            data-testid={`${testid}-primary-badge`}
            className="text-xs px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
          >
            Hauptseite
          </span>
        </div>
      )}

      <ExpenseAttachmentPreview
        key={current.id}
        attachment={current}
        heightClass={heightClass}
        testid={`${testid}-preview`}
      />

      {showPagination && (
        <div
          data-testid={`${testid}-pagination`}
          className="flex items-center justify-center gap-3 text-sm text-white/70"
        >
          <button
            type="button"
            onClick={goPrev}
            disabled={pageIndex === 0}
            aria-label="Vorherige Seite"
            className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            ←
          </button>
          <span data-testid={`${testid}-counter`} className="tabular-nums">
            Seite {pageIndex + 1} von {sorted.length}
          </span>
          <button
            type="button"
            onClick={goNext}
            disabled={pageIndex === sorted.length - 1}
            aria-label="Nächste Seite"
            className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            →
          </button>
        </div>
      )}
    </div>
  );
}

// Test seam
export const __testing = { sortAttachments };
